import { Injectable, signal, type Signal } from '@angular/core';
import type { TemplatePortal } from '@angular/cdk/portal';
import { PaperPanelRequests } from '@ricoh-paper/angular/panels';

/**
 * What a push panel request carries: the content to render (a portal over the
 * opening component's own template, so its state, bindings and outputs stay
 * where they were) and the title the host's header shows, live.
 */
export interface PushPanelTarget {
  readonly portal: TemplatePortal<unknown>;
  readonly heading: Signal<string | null>;
  /**
   * The region's name when there is no heading: a panel that draws its own
   * header (the visitor details panel) passes `heading` null and this.
   */
  readonly label?: Signal<string | null>;
  /** Pin the side panel's width, in px; null keeps Paper's shared width. */
  readonly width?: number | null;
}

/**
 * Visitors' push panels (Paper `docs/panels.md`; the pattern Settings and
 * Manage use). One request service per panel kind, one
 * `paper-panel-host layout="push"` per kind in the shell, inside the
 * `paper-panel-layout` that holds the page canvas. Opening one pushes the
 * canvas over; Escape steps down; the shared width is resizable and
 * remembered.
 *
 * A page never talks to these directly: it wraps the panel's content in
 * `app-push-panel`, which opens and closes the request for it.
 */
@Injectable({ providedIn: 'root' })
export class PushPanels {
  /** A visit: its detail and editor (All Visits, Today). */
  readonly visit = new PaperPanelRequests<PushPanelTarget>('visitors-visit');
  /** A person in the address book: the editor, or a new one. */
  readonly visitor = new PaperPanelRequests<PushPanelTarget>('visitors-visitor');
  /** A host's or organiser's details (All visits: View profile on their card). */
  readonly host = new PaperPanelRequests<PushPanelTarget>('visitors-host');

  /** Every kind, in column order from the canvas outward. */
  readonly all: readonly PaperPanelRequests<PushPanelTarget>[] = [
    this.visit,
    this.visitor,
    this.host,
  ];

  private readonly _hostsMounted = signal(false);
  /**
   * Whether the shell's hosts are on screen. Until they are (a component spec,
   * anything rendered outside the shell), `app-push-panel` draws its content
   * where it stands instead of opening a request nobody will show.
   */
  readonly hostsMounted = this._hostsMounted.asReadonly();

  markHostsMounted(): void {
    this._hostsMounted.set(true);
  }
}
